<template>
  <div class="tai-xiu-full-video">
    <!--
      The stream backend follows the same rule as the cockfight player
      (utils/stream-player.ts): `streamv` in the query, VePlayer by default (streamv=3).
    -->
    <TCVideo
      v-if="playerKind === 'canvas'"
      key="tcvideo"
      class="tai-xiu-full-video__player"
      :videoPlayerReady="true"
      :arena-id="arenaId"
      embedded
    />
    <TCVideoV1
      v-else-if="playerKind === 'tcplayer'"
      key="tcvideov1"
      class="tai-xiu-full-video__player"
      :videoPlayerReady="true"
      :arena-id="arenaId"
      embedded
    />
    <template v-else>
      <VePlayerVideo
        class="tai-xiu-full-video__player"
        :arena-id="arenaId"
        embedded
        @update:videoPlayerReady="isPlaying = $event"
      >
        <template #no-stream>
          <img class="tai-xiu-full-video__feed" :src="placeholder" alt="" />
        </template>
      </VePlayerVideo>
      <!-- Keep the placeholder over the player until the stream actually starts playing. -->
      <img
        v-if="streamUrl && !isPlaying"
        class="tai-xiu-full-video__feed tai-xiu-full-video__cover"
        :src="placeholder"
        alt=""
      />
    </template>
    <!-- Bet placed / payout / unconfirmed bets cancelled, over the video's centre. -->
    <VideoToast :toast="tableId ? videoToast(tableId) : null" :source="tableId" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getStreamPlayerKind } from '@/utils/stream-player';
import VePlayerVideo from '@/components/ve-player.vue';
import TCVideo from '@/components/tc-video.vue';
import TCVideoV1 from '@/components/tc-video-v1.vue';
import VideoToast from '@/components/tai-xiu/video-toast.vue';

export default {
  name: 'TaiXiuFullVideo',
  components: {
    VePlayerVideo,
    TCVideo,
    TCVideoV1,
    VideoToast,
  },
  props: {
    arenaId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      placeholder: require('@/assets/images/tai-xiu-full/video-placeholder.png'),
      isPlaying: false,
    };
  },
  computed: {
    ...mapGetters('general', ['arenaStream', 'streamVersion']),
    ...mapGetters('taiXiu', ['tables', 'videoToast']),
    // The table this stream belongs to, for its toasts.
    tableId() {
      const table = this.tables.find((item) => item.arenaId === this.arenaId);
      return table ? table.id : '';
    },
    playerKind() {
      return getStreamPlayerKind(this.streamVersion);
    },
    // This table's own stream: with two desktop panes streaming, the shared
    // `streamUrl` is whichever arena answered last.
    streamUrl() {
      const stream = this.arenaStream(this.arenaId);
      return (stream && stream.url) || '';
    },
  },
  watch: {
    arenaId(newVal, oldVal) {
      if (oldVal) this.stopArenaStreamPolling(oldVal);
      this.requestStream(newVal);
    },
  },
  mounted() {
    // Requested in mounted (not created) so it runs after the cockfight page's
    // destroyed hook, which stops the shared stream polling.
    this.requestStream(this.arenaId);
  },
  beforeDestroy() {
    if (this.arenaId) this.stopArenaStreamPolling(this.arenaId);
    // Don't leave this table's stream for the cockfight player to pick up.
    this.updateArenaStream({});
  },
  methods: {
    ...mapActions('general', ['getArenaStreamUrl', 'stopArenaStreamPolling', 'updateArenaStream']),
    requestStream(arenaId) {
      this.isPlaying = false;
      // Drop the previous arena's stream so it doesn't play under this table.
      this.updateArenaStream({});
      if (arenaId) this.getArenaStreamUrl(arenaId);
    },
  },
};
</script>

<style lang="scss" scoped>
.tai-xiu-full-video {
  position: relative;
  width: 100%;
  max-width: none;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  overflow: hidden;
  background: #0b213b;

  // Height follows the width at 16:9 via ve-player's own .video-wrapper, like cockfight,
  // so the stream fits with object-fit: contain without bars or cropping.
  &__player {
    width: 100%;
    min-height: 0;
    z-index: auto;

    ::v-deep .video-wrapper {
      background: #0b213b;
    }
  }

  &__feed {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    display: block;
    object-fit: cover;
  }

  &__cover {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
}
</style>
